/**
 * OpenClawConfig — Wizard: Provider → Token → Modelo
 * Refactored: no duplicate fields, step-by-step UX
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { getOcBaseDetectCommand, getOcPaths, getReadFileCommand, getWriteFileCommand, getExecCommand } from '../lib/openclawPaths';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button } from './ui';

interface Props {
  runnerId: string;
  isOnline: boolean;
  openclawPath?: string;
  cloudMachineId?: string;
  onClose?: () => void;
}

interface ProviderDef {
  id: string;
  label: string;
  icon: string;
  placeholder: string;
}

const PROVIDERS: ProviderDef[] = [
 { id: 'elon-router', label: 'LLM Router (padrão)', icon: '', placeholder: 'Token gerado automaticamente' },
 { id: 'anthropic', label: 'Anthropic', icon: '', placeholder: 'sk-ant-api03-...' },
 { id: 'openai', label: 'OpenAI', icon: '', placeholder: 'sk-...' },
 { id: 'google', label: 'Google', icon: '', placeholder: 'AIza...' },
];

export function OpenClawConfig({ runnerId, isOnline, openclawPath, cloudMachineId, onClose }: Props) {
  const [selectedProvider, setSelectedProvider] = useState('elon-router');
  const [providerKeys, setProviderKeys] = useState<Record<string, string>>({});
  const [configuredProviders, setConfiguredProviders] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<Array<{ id: string; name: string; description: string }>>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [editingToken, setEditingToken] = useState(false);
  const [tokenDraft, setTokenDraft] = useState('');
  const [changes, setChanges] = useState<Record<string, boolean>>({});
  // Ref to track user interaction — immune to stale closures in async callbacks
  const userChangedProvider = useRef(false);
  const [liveStatus, setLiveStatus] = useState<{
    selectedProvider: string;
    tokens: Record<string, string>;
    defaultModel: string;
    configUpdatedAt: string | null;
    source: string;
  } | null>(null);
  const [loadingLiveStatus, setLoadingLiveStatus] = useState(false);

  const hasChanges = Object.values(changes).some(v => v);
  const currentProviderDef = (PROVIDERS.find(p => p.id === selectedProvider) ?? PROVIDERS[0])!;
  const currentToken = providerKeys[selectedProvider] || '';
  const hasToken = !!currentToken;
  const isElonRouter = selectedProvider === 'elon-router';

  // ─── Load live status ───
  async function loadOpenClawLiveStatus() {
    if (!cloudMachineId) return;
    setLoadingLiveStatus(true);
    try {
      const { api } = await import('../api/client.js');
      const res = await api.get(`/cloud/machines/${cloudMachineId}/openclaw-live-status`) as any;
      const data = res?.data || res;
      if (data) {
        setLiveStatus(data);
        if (data.selectedProvider && !userChangedProvider.current) setSelectedProvider(data.selectedProvider);
        if (data.defaultModel && !userChangedProvider.current) setSelectedModel(data.defaultModel);
      }
    } catch (e) {
      console.warn('[loadOpenClawLiveStatus] Error:', e);
    } finally {
      setLoadingLiveStatus(false);
    }
  }

  // ─── Sync LLM info to D1 (fire-and-forget cache for listing) ───
  function syncLlmToD1(provider: string, model: string) {
    if (!cloudMachineId) return;
    api.put(`/cloud/machines/${cloudMachineId}/agent-info`, {
      agent_provider: provider,
      agent_model: model,
    }).catch(e => console.warn('[Config] D1 sync failed:', e));
  }

  // ─── Load config from machine ───
  async function loadCurrentConfig() {
    function applyConfig(cfg: any) {
      const configured: string[] = [];
      const keys: Record<string, string> = {};
      const envVars = cfg?.env?.vars || {};

      const elonRouterToken = cfg?.models?.providers?.elon?.apiKey;
      if (elonRouterToken) { configured.push('elon-router'); keys['elon-router'] = elonRouterToken; }
      if (envVars.ANTHROPIC_API_KEY) { configured.push('anthropic'); keys['anthropic'] = envVars.ANTHROPIC_API_KEY; }
      if (envVars.OPENAI_API_KEY) { configured.push('openai'); keys['openai'] = envVars.OPENAI_API_KEY; }
      if (envVars.GOOGLE_API_KEY) { configured.push('google'); keys['google'] = envVars.GOOGLE_API_KEY; }

      const currentProvider = cfg?._selectedProvider || 'elon-router';
      // Only set provider from config if user hasn't manually changed it
      if (!userChangedProvider.current) {
        if (configured.includes(currentProvider)) {
          setSelectedProvider(currentProvider);
        } else if (!currentProvider || !configured.includes(currentProvider)) {
          setSelectedProvider(configured.length > 0 ? configured[0]! : 'elon-router');
        }
      }

      setConfiguredProviders(configured);
      setProviderKeys(keys);
      return configured.length > 0;
    }

    // Strategy 1: Read from machine via runner
    let loadedFromRunner = false;
    try {
      const { runCommandDirect } = await import('../lib/runCommand.js');
      const { api } = await import('../api/client.js');
      const baseRaw = await runCommandDirect(api, runnerId, getOcBaseDetectCommand(openclawPath));
      const base = baseRaw.trim() || '/data/.openclaw';
      const paths = getOcPaths(base);
      const output = await runCommandDirect(api, runnerId, getReadFileCommand(paths.configJson, openclawPath));
      if (!output.includes('NO_FILE') && output.includes('{')) {
        const match = output.match(/\{[\s\S]*\}/);
        if (match) {
          const parsedCfg = JSON.parse(match[0]);
          loadedFromRunner = applyConfig(parsedCfg);
          if (loadedFromRunner) {
            const cfgProvider = parsedCfg?._selectedProvider || 'elon-router';
            const cfgModel = parsedCfg?.agents?.defaults?.model?.primary || '';
            syncLlmToD1(cfgProvider, cfgModel);
            return;
          }
        }
      }
    } catch (err: any) {
      console.warn('[loadCurrentConfig] Runner read failed:', err?.message);
    }

    // Strategy 2: DB fallback
    if (cloudMachineId && !loadedFromRunner) {
      try {
        const { api } = await import('../api/client.js');
        const res = await api.get(`/cloud/machines/${cloudMachineId}/openclaw-saved-config`) as any;
        const data = res?.data || res;
        if (data?.config) {
          const cfg = typeof data.config === 'string' ? JSON.parse(data.config) : data.config;
          if (applyConfig(cfg)) return;
        }
      } catch (err: any) {
        console.warn('[loadCurrentConfig] DB fallback failed:', err?.message);
      }
    }

    if (!loadedFromRunner) {
      setConfiguredProviders(['elon-router']);
      setProviderKeys({ 'elon-router': '' });
      setSelectedProvider('elon-router');
    }
  }

  useEffect(() => {
    if (cloudMachineId) loadOpenClawLiveStatus();
    if (isOnline && runnerId) loadCurrentConfig();
  }, [runnerId, isOnline, cloudMachineId]);

  // ─── Fetch models for provider ───
  async function fetchModelsForProvider(provider: string, token: string) {
    if (!cloudMachineId || !token || provider === 'elon-router') return;
    setLoadingModels(true);
    try {
      const { api } = await import('../api/client.js');
      const result = await api.post<{ models: Array<{ id: string; name: string; description: string }>; count: number }>(
        `/cloud/machines/${cloudMachineId}/fetch-llm-models`,
        { provider, token }
      );
      if (result?.models?.length) {
        setAvailableModels(result.models);
      }
    } catch (e) {
      console.error('[FetchModels] Error:', e);
      setAvailableModels([]);
    } finally {
      setLoadingModels(false);
    }
  }

  // ─── Provider change ───
  function handleProviderChange(providerId: string) {
    console.log('[PROVIDER_CHANGE]', providerId);
    userChangedProvider.current = true;
    setSelectedProvider(providerId);
    setChanges(prev => ({ ...prev, selectedProvider: true }));
    setAvailableModels([]);
    setSelectedModel('');
    setEditingToken(false);
    setTokenDraft('');
    // If provider already has a token and it's not elon-router, fetch models
    const existingToken = providerKeys[providerId];
    if (existingToken && providerId !== 'elon-router') {
      fetchModelsForProvider(providerId, existingToken);
    }
  }

  // ─── Save token ───
  async function handleTokenSave() {
    const token = tokenDraft.trim();
    if (!token) return;

    const newKeys = { ...providerKeys, [selectedProvider]: token };
    setProviderKeys(newKeys);
    if (!configuredProviders.includes(selectedProvider)) {
      setConfiguredProviders(prev => [...prev, selectedProvider]);
    }
    setEditingToken(false);
    setTokenDraft('');

    if (cloudMachineId) {
      setSaving(true);
      try {
        const { api } = await import('../api/client.js');
        await api.post(`/cloud/machines/${cloudMachineId}/configure-openclaw`, {
          selectedProvider,
          providerKeys: newKeys,
        });
 setSaveResult({ ok: true, msg: 'Token salvo!' });
        fetchModelsForProvider(selectedProvider, token);
        setTimeout(() => { loadCurrentConfig().catch(console.error); loadOpenClawLiveStatus().catch(console.error); }, 1000);
        setTimeout(() => setSaveResult(null), 3000);
      } catch (err: any) {
 setSaveResult({ ok: false, msg: ` ${err?.message || 'Erro ao salvar token'}` });
        setTimeout(() => setSaveResult(null), 4000);
      } finally {
        setSaving(false);
      }
    }
  }

  // ─── Save all changes ───
  async function handleSaveChanges() {
    if (!cloudMachineId || !hasChanges) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const { api } = await import('../api/client.js');
      const payload: any = { selectedProvider };
      if (Object.keys(providerKeys).length > 0) payload.providerKeys = providerKeys;
      if (changes.defaultModel && selectedModel) payload.defaultModel = selectedModel;

      if (openclawPath) payload.openclawPath = openclawPath;
      await api.post(`/cloud/machines/${cloudMachineId}/configure-openclaw`, payload);
 setSaveResult({ ok: true, msg: 'Configurações salvas com sucesso!' });
      syncLlmToD1(selectedProvider, selectedModel || '');
      setChanges({});
      userChangedProvider.current = false;
      setTimeout(() => { loadCurrentConfig().catch(console.error); loadOpenClawLiveStatus().catch(console.error); }, 500);
      setTimeout(() => setSaveResult(null), 3000);
    } catch (err: any) {
 setSaveResult({ ok: false, msg: ` ${err?.message || 'Falha ao salvar'}` });
    } finally {
      setSaving(false);
    }
  }

  // ─── Legacy save for non-cloud machines ───
  async function handleSaveLegacy() {
    setSaving(true);
    setSaveResult(null);
    try {
      const { runCommandDirect } = await import('../lib/runCommand.js');
      const { api } = await import('../api/client.js');

      // Detect base path (flexible: Docker, /data/.openclaw, ~/.openclaw, etc)
      const baseRaw = await runCommandDirect(api, runnerId, getOcBaseDetectCommand(openclawPath));
      const base = baseRaw.trim() || '/data/.openclaw';
      const paths = getOcPaths(base);

      const output = await runCommandDirect(api, runnerId, getReadFileCommand(paths.configJson, openclawPath));
      const clean = output.replace('NO_FILE', '{}');
      const match = clean.match(/\{[\s\S]*\}/);
      let cfg: any = {};
      try { cfg = JSON.parse(match ? match[0] : '{}'); } catch { cfg = {}; }

      if (!cfg.agents) cfg.agents = {};
      if (!cfg.agents.defaults) cfg.agents.defaults = {};
      cfg.agents.defaults.model = { primary: selectedModel };
      const configStr = JSON.stringify(cfg, null, 2);
      await runCommandDirect(api, runnerId, getExecCommand(`mkdir -p ${base}`, openclawPath));
      await runCommandDirect(api, runnerId, getWriteFileCommand(paths.configJson, configStr, openclawPath));
      await runCommandDirect(api, runnerId, getExecCommand('openclaw gateway restart 2>/dev/null || true', openclawPath));
      setSaveResult({ ok: true, msg: 'Configuração salva! OpenClaw reiniciado.' });
    } catch (err: any) {
      setSaveResult({ ok: false, msg: `Erro: ${err.message || 'Falha ao salvar'}` });
    } finally {
      setSaving(false);
    }
  }

  if (!isOnline) {
    return (
      <div style={{
        padding: 24, marginBottom: 20, borderRadius: 14,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        textAlign: 'center', color: 'var(--text-muted)',
      }}>
 <span style={{ fontSize: 32 }}></span>
        <p style={{ margin: '8px 0 0' }}>Máquina offline. Conecte para configurar.</p>
      </div>
    );
  }

  return (
    <div style={{
      marginBottom: 20, borderRadius: 14, overflow: 'hidden',
      border: `1px solid ${T.accentBgStrong}`,
      background: 'var(--bg-card-solid)',
    }}>
      {/* ─── Header ─── */}
      <div style={{
        padding: '16px 20px',
        background: `linear-gradient(135deg, ${T.accentBg}, ${T.primaryBg})`,
        borderBottom: `1px solid ${T.accentBg}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <span style={{ fontSize: 22 }}></span>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--secondary)' }}>Configurar Inteligência</h3>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              Escolha o LLM que alimenta seu agente
            </div>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-muted)',
            fontSize: 18, cursor: 'pointer', padding: 4,
          }}></Button>
        )}
      </div>

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ═══ FRIENDLY INTRO ═══ */}
        <div style={{
          padding: '14px 16px', borderRadius: 10,
          background: `linear-gradient(135deg, ${T.accentBgSubtle}, rgba(168,85,247,0.06))`,
          border: '1px solid rgba(168,85,247,0.2)',
        }}>
          <div style={{ fontSize: 13, color: T.primary, lineHeight: 1.5 }}>
 <strong>O que é Inteligência do Agente?</strong><br />
            É o modelo de IA que seu agente usa para pensar e responder. Escolha entre LLM Router (padrão), Anthropic, OpenAI ou Google.
          </div>
        </div>

        {/* ═══ STATUS BANNER ═══ */}
        {liveStatus && (
          <div style={{
            padding: 16, borderRadius: 12,
            background: T.successBg,
            border: `1px solid ${T.successBgStrong}`,
          }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: T.success, marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: T.success,
                boxShadow: '0 0 8px #34D399',
                display: 'inline-block',
              }} />
              Config da Máquina — LIVE
              <span style={{
                fontSize: 9, padding: '2px 6px', borderRadius: 4, marginLeft: 'auto',
                background: liveStatus.source === 'runner' ? T.successBg : 'rgba(239,193,48,0.2)',
                color: liveStatus.source === 'runner' ? T.success : T.warning,
                fontWeight: 600,
              }}>
                {liveStatus.source === 'runner' ? '● LIVE' : liveStatus.source === 'database' ? '● DB Cache' : '● Sem config'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 2 }}>
 <div>Provider: <strong style={{ color: T.purple }}>{PROVIDERS.find(p => p.id === liveStatus.selectedProvider)?.label || liveStatus.selectedProvider || '—'}</strong></div>
 <div>Modelo: <strong style={{ color: T.purple }}>{liveStatus.defaultModel || '—'}</strong></div>
              {liveStatus.configUpdatedAt && (
 <div>Atualizado: <strong style={{ color: T.textSecondary }}>{new Date(liveStatus.configUpdatedAt).toLocaleString('pt-BR')}</strong></div>
              )}
            </div>
          </div>
        )}
        {loadingLiveStatus && !liveStatus && (
          <div style={{
            padding: 12, borderRadius: 10,
            background: T.accentBg, border: `1px solid ${T.accentBg}`,
            fontSize: 11, color: 'var(--text-muted)', textAlign: 'center',
          }}>
 Lendo configuração da máquina...
          </div>
        )}

        {/* ═══ PASSO 1: SELECIONAR PROVIDER (radio buttons) ═══ */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 8 }}>
 Escolha a Inteligência <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>— passo 1</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {PROVIDERS.map(p => {
              const isSelected = selectedProvider === p.id;
              const isConfigured = configuredProviders.includes(p.id);
              return (
                <Button variant="ghost"
                  key={p.id}
                  onClick={() => handleProviderChange(p.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 10, textAlign: 'left',
                    background: isSelected ? `${T.accentBg}` : 'transparent',
                    border: isSelected ? `2px solid ${T.accentBgStrong}` : '1px solid rgba(255,255,255,0.06)',
                    color: 'var(--text)', cursor: 'pointer', transition: 'all 0.15s',
                    width: '100%',
                  }}
                >
                  {/* Radio circle */}
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: isSelected ? '2px solid #8B5CF6' : `2px solid ${T.whiteAlpha15}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSelected && (
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: T.purple,
                      }} />
                    )}
                  </div>
                  <span style={{ fontSize: 18 }}>{p.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, flex: 1 }}>{p.label}</span>
                  {isConfigured && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                      background: T.successBg, color: T.success,
                      border: `1px solid ${T.successBgStrong}`,
                    }}>Token</span>
                  )}
                </Button>
              );
            })}
          </div>
        </div>

        {/* ═══ PASSO 2: TOKEN (apenas para provider selecionado) ═══ */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 8 }}>
 Autenticar com {currentProviderDef.label} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>— passo 2</span>
          </label>

          {hasToken && !editingToken ? (
            /* Token exists — show masked with copy/edit */
            <div style={{
              display: 'flex', gap: 8, alignItems: 'center',
              padding: '10px 14px', borderRadius: 10,
              background: T.successBg, border: `1px solid ${T.successBgStrong}`,
            }}>
              <input
                type="password"
                value={currentToken}
                disabled
                style={{
                  flex: 1, padding: '8px 10px', borderRadius: 6,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  color: 'var(--text-muted)', fontSize: 12, fontFamily: 'monospace',
                  cursor: 'not-allowed',
                }}
              />
              <Button variant="ghost"
                onClick={() => {
                  navigator.clipboard.writeText(currentToken);
                  setTokenCopied(true);
                  setTimeout(() => setTokenCopied(false), 2000);
                }}
                style={{
                  padding: '8px 12px', borderRadius: 6,
                  background: T.accentBg, border: `1px solid ${T.accentBgStrong}`,
                  color: T.purple, fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
 {tokenCopied ? 'Copiado!' : 'Copiar'}
              </Button>
              {!isElonRouter && (
                <Button variant="ghost"
                  onClick={() => { setEditingToken(true); setTokenDraft(currentToken); }}
                  style={{
                    padding: '8px 12px', borderRadius: 6,
                    background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
                    color: T.primary, fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
 Editar
                </Button>
              )}
            </div>
          ) : !hasToken || editingToken ? (
            /* No token or editing — show input */
            isElonRouter && !editingToken ? (
              <div style={{
                padding: '12px 14px', borderRadius: 10,
                background: T.accentBg, border: `1px solid ${T.accentBg}`,
                fontSize: 12, color: 'var(--text-muted)',
              }}>
 Token do LLM Router é gerado automaticamente ao salvar.
              </div>
            ) : (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'center',
                padding: '10px 14px', borderRadius: 10,
                background: T.bgOverlay, border: `1px solid ${T.accentBg}`,
              }}>
                <input
                  type="text"
                  value={tokenDraft}
                  onInput={(e: any) => setTokenDraft(e.target.value)}
                  placeholder={currentProviderDef.placeholder}
                  autoFocus
                  style={{
                    flex: 1, padding: '8px 10px', borderRadius: 6,
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    color: 'var(--text)', fontSize: 12, fontFamily: 'monospace', outline: 'none',
                  }}
                />
                <Button variant="ghost"
                  onClick={handleTokenSave}
                  disabled={!tokenDraft.trim() || saving}
                  style={{
                    padding: '8px 14px', borderRadius: 6,
                    background: tokenDraft.trim() ? T.successBg : T.bgCardHover,
                    border: `1px solid ${T.successBgStrong}`,
                    color: tokenDraft.trim() ? T.success : 'var(--text-muted)',
                    fontSize: 11, fontWeight: 700,
                    cursor: tokenDraft.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap',
                    opacity: tokenDraft.trim() ? 1 : 0.5,
                  }}
                >
 {saving ? '...' : 'Salvar Token'}
                </Button>
                {editingToken && (
                  <Button variant="ghost"
                    onClick={() => { setEditingToken(false); setTokenDraft(''); }}
                    style={{
                      padding: '8px 10px', borderRadius: 6,
                      background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
                      color: T.danger, fontSize: 11, fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                                      </Button>
                )}
              </div>
            )
          ) : null}

          {isElonRouter && hasToken && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              Token real gerado e salvo no OpenClaw
            </div>
          )}
        </div>

        {/* ═══ PASSO 3: MODELO PADRÃO (apenas se token OK) ═══ */}
        {(hasToken || isElonRouter) && (availableModels.length > 0 || loadingModels || selectedModel) && (
          <div>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 8 }}>
 Escolha o Modelo <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>— passo 3
                {availableModels.length > 0 && ` (${availableModels.length} disponíveis)`}
              </span>
            </label>
            {loadingModels ? (
              <div style={{
                padding: '12px 16px', borderRadius: 10,
                background: T.accentBg, border: `1px solid ${T.accentBg}`,
                fontSize: 12, color: 'var(--text-muted)', textAlign: 'center',
              }}>
 <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
              </div>
            ) : availableModels.length > 0 ? (
              <select
                value={selectedModel}
                onChange={(e: any) => {
                  setSelectedModel(e.target.value);
                  setChanges(prev => ({ ...prev, defaultModel: true }));
                }}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 10,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  color: 'var(--text)', fontSize: 13, outline: 'none',
                  cursor: 'pointer', boxSizing: 'border-box',
                }}
              >
                <option value="">Selecione um modelo...</option>
                {availableModels.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            ) : selectedModel ? (
              <div style={{
                padding: '10px 14px', borderRadius: 10,
                background: T.accentBg, border: `1px solid ${T.accentBg}`,
                fontSize: 12, color: T.purple,
              }}>
                {selectedModel}
              </div>
            ) : null}
            {selectedModel && availableModels.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Modelo selecionado: <strong style={{ color: T.purple }}>{selectedModel}</strong>
              </div>
            )}
          </div>
        )}

        {/* ═══ Unsaved changes warning ═══ */}
        {hasChanges && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(239,193,48,0.08)', border: '1px solid rgba(239,193,48,0.3)',
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontWeight: 600, color: T.warning,
          }}>
 Alterações não salvas
          </div>
        )}

        {/* ═══ Save result ═══ */}
        {saveResult && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: saveResult.ok ? T.successBg : T.dangerBg,
            border: saveResult.ok ? `1px solid ${T.successBgStrong}` : `1px solid ${T.dangerBgStrong}`,
            fontSize: 12, fontWeight: 600, color: saveResult.ok ? T.success : T.danger,
          }}>
            {saveResult.msg}
          </div>
        )}

        {/* ═══ Save button ═══ */}
        <Button variant="ghost"
          onClick={cloudMachineId ? handleSaveChanges : handleSaveLegacy}
          disabled={(!hasChanges && !!cloudMachineId) || saving}
          style={{
            width: '100%', padding: '12px 16px', borderRadius: 10,
            background: (hasChanges || !cloudMachineId) && !saving
              ? `linear-gradient(135deg, ${T.successBgStrong}, ${T.successBg})`
              : T.accentBg,
            border: (hasChanges || !cloudMachineId) && !saving
              ? `2px solid ${T.successBgStrong}`
              : `1px solid ${T.accentBg}`,
            color: (hasChanges || !cloudMachineId) && !saving ? T.success : 'var(--text-muted)',
            fontSize: 13, fontWeight: 700,
            cursor: (hasChanges || !cloudMachineId) && !saving ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: (hasChanges || !cloudMachineId) && !saving ? 1 : 0.5,
            transition: 'all 0.2s',
          }}
        >
          {saving ? (
 <><span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: 14 }}></span>Salvando...</>
          ) : (
            <>Salvar Alterações</>
          )}
        </Button>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
